import { useCallback, useEffect, useState, useRef } from "react";
import { useCollaborationStore, UserSession } from "@/stores/collaboration";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Position, Viewport } from "@/types";
import { logger } from "@/utils";

export interface PresenceState {
  activeUsers: UserSession[];
  userCursors: Map<string, Position>;
  userSelections: Map<string, string[]>;
  onlineUsers: string[];
  idleUsers: string[];
  offlineUsers: string[];
}

export interface PresenceActions {
  updateUserCursor: (userId: string, position: Position) => void;
  updateUserSelection: (userId: string, selection: string[]) => void;
  updateUserPresence: (
    userId: string,
    status: "online" | "idle" | "offline",
  ) => void;
  getUsersInViewport: (viewport: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => UserSession[];
  getUserById: (userId: string) => UserSession | undefined;
  getUserColor: (userId: string) => string;
  isUserActive: (userId: string, timeThreshold?: number) => boolean;
}

export const usePresence = () => {
  const {
    activeUsers,
    userPresence,
    currentUser,
    updateUserCursor: storeUpdateUserCursor,
    updateUserSelection: storeUpdateUserSelection,
    updateUserPresence: storeUpdateUserPresence,
  } = useCollaborationStore();

  const { viewport } = useEditorStore();

  // Local state for optimized rendering
  const [visibleUsers, setVisibleUsers] = useState<UserSession[]>([]);
  const [lastViewportUpdate, setLastViewportUpdate] = useState<number>(0);

  // Throttling refs
  const cursorUpdateTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const presenceUpdateTimer = useRef<NodeJS.Timeout | null>(null);
  const viewportCalculationTimer = useRef<NodeJS.Timeout | null>(null);

  // Memoized state calculations
  const userCursors = new Map(
    Array.from(activeUsers.values())
      .filter((user) => user.cursor)
      .map((user) => [user.userId, user.cursor!]),
  );

  const userSelections = new Map(
    Array.from(activeUsers.values())
      .filter((user) => user.selection?.length)
      .map((user) => [user.userId, user.selection!]),
  );

  const onlineUsers = Array.from(activeUsers.values())
    .filter((user) => userPresence.get(user.userId) === "online")
    .map((user) => user.userId);

  const idleUsers = Array.from(activeUsers.values())
    .filter((user) => userPresence.get(user.userId) === "idle")
    .map((user) => user.userId);

  const offlineUsers = Array.from(activeUsers.values())
    .filter((user) => userPresence.get(user.userId) === "offline")
    .map((user) => user.userId);

  /**
   * Update user cursor with throttling
   */
  const updateUserCursor = useCallback(
    (userId: string, position: Position) => {
      // Clear existing timer for this user
      const existingTimer = cursorUpdateTimers.current.get(userId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer for throttled update
      const timer = setTimeout(() => {
        storeUpdateUserCursor(userId, position);
        cursorUpdateTimers.current.delete(userId);
      }, 16); // ~60fps throttling

      cursorUpdateTimers.current.set(userId, timer);
    },
    [storeUpdateUserCursor],
  );

  /**
   * Update user selection
   */
  const updateUserSelection = useCallback(
    (userId: string, selection: string[]) => {
      storeUpdateUserSelection(userId, selection);
    },
    [storeUpdateUserSelection],
  );

  /**
   * Update user presence with debouncing
   */
  const updateUserPresence = useCallback(
    (userId: string, status: "online" | "idle" | "offline") => {
      // Clear existing timer
      if (presenceUpdateTimer.current) {
        clearTimeout(presenceUpdateTimer.current);
      }

      // Debounce presence updates to avoid rapid status changes
      presenceUpdateTimer.current = setTimeout(() => {
        storeUpdateUserPresence(userId, status);
        presenceUpdateTimer.current = null;
      }, 1000); // 1 second debouncing
    },
    [storeUpdateUserPresence],
  );

  /**
   * Get users visible in current viewport (with culling optimization)
   */
  const getUsersInViewport = useCallback(
    (viewport: {
      x: number;
      y: number;
      width: number;
      height: number;
    }): UserSession[] => {
      const now = Date.now();

      // Throttle viewport calculations for performance
      if (now - lastViewportUpdate < 100) {
        // 100ms throttle
        return visibleUsers;
      }

      const users = Array.from(activeUsers.values()).filter((user) => {
        if (!user.cursor) return false;

        // Check if cursor is within viewport bounds
        const { x, y } = user.cursor;
        const buffer = 50; // Add buffer to catch cursors just outside viewport

        return (
          x >= viewport.x - buffer &&
          x <= viewport.x + viewport.width + buffer &&
          y >= viewport.y - buffer &&
          y <= viewport.y + viewport.height + buffer
        );
      });

      // Update with throttling
      if (viewportCalculationTimer.current) {
        clearTimeout(viewportCalculationTimer.current);
      }

      viewportCalculationTimer.current = setTimeout(() => {
        setVisibleUsers(users);
        setLastViewportUpdate(now);
      }, 50);

      return users;
    },
    [activeUsers, visibleUsers, lastViewportUpdate],
  );

  /**
   * Get user by ID
   */
  const getUserById = useCallback(
    (userId: string): UserSession | undefined => {
      return activeUsers.get(userId);
    },
    [activeUsers],
  );

  /**
   * Get user color
   */
  const getUserColor = useCallback(
    (userId: string): string => {
      const user = activeUsers.get(userId);
      return user?.color || "#888888";
    },
    [activeUsers],
  );

  /**
   * Check if user is active based on last activity
   */
  const isUserActive = useCallback(
    (
      userId: string,
      timeThreshold: number = 5 * 60 * 1000, // 5 minutes default
    ): boolean => {
      const user = activeUsers.get(userId);
      if (!user) return false;

      const timeSinceActivity = Date.now() - user.lastActivity.getTime();
      return timeSinceActivity < timeThreshold;
    },
    [activeUsers],
  );

  /**
   * Auto-update presence based on activity
   */
  useEffect(() => {
    if (!currentUser) return;

    const checkActivity = () => {
      const timeSinceActivity = Date.now() - currentUser.lastActivity.getTime();
      const currentStatus = userPresence.get(currentUser.userId);

      if (timeSinceActivity > 5 * 60 * 1000 && currentStatus !== "idle") {
        // 5 minutes
        updateUserPresence(currentUser.userId, "idle");
      } else if (timeSinceActivity < 30 * 1000 && currentStatus !== "online") {
        // 30 seconds
        updateUserPresence(currentUser.userId, "online");
      }
    };

    const interval = setInterval(checkActivity, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [currentUser, userPresence, updateUserPresence]);

  /**
   * Cleanup timers on unmount
   */
  useEffect(() => {
    return () => {
      // Clear cursor update timers
      cursorUpdateTimers.current.forEach((timer) => clearTimeout(timer));
      cursorUpdateTimers.current.clear();

      // Clear presence update timer
      if (presenceUpdateTimer.current) {
        clearTimeout(presenceUpdateTimer.current);
      }

      // Clear viewport calculation timer
      if (viewportCalculationTimer.current) {
        clearTimeout(viewportCalculationTimer.current);
      }
    };
  }, []);

  /**
   * Performance monitoring for presence updates
   */
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const logStats = () => {
        const stats = {
          totalUsers: activeUsers.size,
          onlineUsers: onlineUsers.length,
          idleUsers: idleUsers.length,
          offlineUsers: offlineUsers.length,
          visibleUsers: visibleUsers.length,
          activeTimers: cursorUpdateTimers.current.size,
        };

        logger.debug("Presence stats:", stats);
      };

      const interval = setInterval(logStats, 30000); // Log every 30 seconds
      return () => clearInterval(interval);
    }
  }, [activeUsers, onlineUsers, idleUsers, offlineUsers, visibleUsers]);

  const state: PresenceState = {
    activeUsers: Array.from(activeUsers.values()),
    userCursors,
    userSelections,
    onlineUsers,
    idleUsers,
    offlineUsers,
  };

  const actions: PresenceActions = {
    updateUserCursor,
    updateUserSelection,
    updateUserPresence,
    getUsersInViewport,
    getUserById,
    getUserColor,
    isUserActive,
  };

  return { ...state, ...actions };
};
